import { PlatformLocation } from '@angular/common';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, HostListener, Optional, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { FooterButtonComponent } from '../../shared/footer-button/footer-button.component';
import type { RCRFeeScheduleResponse } from '../../shared/interfaces/rcrfeescheduleresponse.interface';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { FeeScheduleService } from '../../shared/services/fee-schedule.service';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { UserService } from '../../shared/services/user.service';
import { StringConstants } from '../constants/string-constants';
import { EnterBalancesValidationServiceService } from '../services/enter-balances-validation-service.service';
import type { RCRExchangeFeeResponse } from '../services/interfaces/RCRExchangeFeeResponse';
import type { CommitExchangeResponse } from '../services/interfaces/commit-exchange-response';
import { PassdataService } from '../services/passdata.service';
import { RcrExchangeNavigationServiceService } from '../services/rcr-exchange-navigation-service.service';
import { RecombinationsService } from '../services/recombinations.service';
import { DateSelectedService } from '../shared/date-selected.service';
import { RcrStickyFooterConfig } from '../shared/sticky-toolbar/rcr-exchange-footer.component';

export interface WarningMessage {
  title: string;
  message: string;
  type: string;
}

@Component({
  standalone: false,
  selector: 'invd-review-and-commit',
  templateUrl: './review-and-commit.component.html',
  styleUrls: ['./review-and-commit.component.scss'],
})
export class ReviewAndCommitComponent implements OnInit, AfterViewInit, OnDestroy {
  private submitFooter: Array<RcrStickyFooterConfig> = [
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      0,
      'Cancel',
      null,
      null,
      (_value) => {
        this.stickyBarCancelClick();
      },
      { isPrimary: false },
    ),
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      1,
      'Submit',
      null,
      null,
      (_value) => {
        this.stickyBarBtnClick();
      },
      { isPrimary: true },
    ),
  ];

  private commitFooter: Array<RcrStickyFooterConfig> = [
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      0,
      'Cancel',
      null,
      null,
      (_value) => {
        this.stickyBarCancelClick();
      },
      { isPrimary: false },
    ),
    new RcrStickyFooterConfig(
      FooterButtonComponent,
      1,
      'Commit',
      null,
      null,
      (_value) => {
        this.stickyBarBtnClick();
      },
      { isPrimary: true },
    ),
  ];
  public stickFooterComponents: Array<RcrStickyFooterConfig> = this.commitFooter;

  /*@ViewChild(StickyInputToolbarComponent)
   public stickyToolbarComponent: StickyInputToolbarComponent;*/
  public rcrResponseData: RCRExchangeFeeResponse;
  public screenTitle: string;
  public screenSubTitle: string;
  public exchangeType: string;
  public displayRecomColumn: boolean;
  public isAtSettlementFlow: boolean;
  public showFeeSheduleModelWindow = false;
  public dealNumber: string;
  public tempFeeCommentStr: string;
  public feeComment: string;
  public overrideFeeIndicator = false;
  public displayedFeeTicks: number = null;
  public displayedFeeDollars: number = null;
  public showBanner = false;
  public warningMessages: WarningMessage[];
  public showCancelExchangeConfirmation = false;
  public isCancelConfirmation = true;

  public isAllowToNavigateAway = false;
  public destinationUrl = '/';
  public displayPricingRule = false;
  public prospectSupplURL = '';
  public showBannerExitIconNavigationPopup = false;
  public isFeeScheduleLinkClicked = true;
  public isInternalUsr: boolean;

  protected changeFeesDropDownList: Array<any> = [
    { id: '0', label: 'Manual' },
    { id: '1', label: 'No Fee' },
    { id: '2', label: '0.25/32' },
    { id: '3', label: '0.50/32' },
    { id: '4', label: '0.75/32' },
    { id: '5', label: '1.00/32' },
    { id: '6', label: '1.25/32' },
    { id: '7', label: '1.50/32' },
    { id: '8', label: '1.75/32' },
    { id: '9', label: '2.00/32' },
    { id: '10', label: '2.25/32' },
    { id: '11', label: '2.50/32' },
    { id: '12', label: '2.75/32' },
    { id: '13', label: '3.00/32' },
    { id: '14', label: '3.25/32' },
    { id: '15', label: '3.50/32' },
    { id: '16', label: '3.75/32' },
    { id: '17', label: '4.00/32' },
  ];

  public showChangeFeeModelWindow = false;
  public isChangeFeeDropdownOpen = false;

  protected manualFeesAmount: number = null;
  protected selectedChangeFeesItem: any = null;

  private rcrService = inject(RecombinationsService);
  @Optional() private feeScheduleService = inject(FeeScheduleService);
  private router = inject(Router);
  private passDataService = inject(PassdataService);
  private userService = inject(UserService);
  private rcrNavigationService = inject(RcrExchangeNavigationServiceService);
  private enterBalancesValidationService = inject(EnterBalancesValidationServiceService);
  private location = inject(PlatformLocation);
  private errorHandlerService = inject(ErrorHandlerService);
  private fmLoadingPageService = inject(FmLoadingPageService);
  private dateSelectedService = inject(DateSelectedService);
  private cdr = inject(ChangeDetectorRef);

  @HostListener('window:beforeunload', ['$event'])
  refreshBtnClick($event) {
    $event.returnValue = '';
  }

  ngOnInit() {
    this.isInternalUsr = this.userService.isInternalUser;
    this.isFeeScheduleLinkClicked = true;
    this.rcrResponseData = this.rcrService.getRCRExchangeFeeData();
    this.exchangeType = this.passDataService.getExchangeType();
    this.enableStickyFooterCommitButton();
    this.displayBanner();
    this.displayPricingModel();
    this.listenToBannerExitReviewAndCommitClick();
  }

  ngAfterViewInit() {
    this.dealNumber = this.passDataService.getRecombinationDataCache().recombinations.dealNumber;
    this.updateTitleOrTable();
    this.displayedFeeTicks = this.rcrService.getRCRExchangeFeeData().feeTicks;
    this.displayedFeeDollars = this.rcrService.getRCRExchangeFeeData().feeDollars;
    this.updateStickyBarCommitButtonText();
    this.navigationController();
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.rcrService.setRCRExchangeFeeResponse(null);
    this.rcrService.setExpandableSMBSExchangeResponse(null);
    this.rcrService.setFeeScheduleData(null);
    this.rcrService.setRecombinationRequestData(null);
    this.passDataService.setRecombinationDataCacheWithoutNullGroup(null);
    this.resetRCRNavigationState();
  }

  private navigationController() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (!this.isAtSettlementFlow) {
          if (event.url.indexOf('confirm') > 0) {
            this.isAllowToNavigateAway = true;
          } else if (event.url.indexOf('enter-balances') > 0) {
            this.isAllowToNavigateAway = true;
          } else if (event.url.indexOf('404') > 0) {
            this.isAllowToNavigateAway = true;
          } else if (event.url.indexOf('error') > 0) {
            this.isAllowToNavigateAway = true;
          } else {
            this.stickyBarCancelClick();
            this.isAllowToNavigateAway = false;
            this.destinationUrl = event.url;
          }
        } else {
          if (event.url.indexOf('confirm') > 0) {
            this.isAllowToNavigateAway = true;
          } else if (event.url.indexOf('enter-takedowns') > 0) {
            this.isAllowToNavigateAway = true;
          } else if (event.url.indexOf('404') > 0) {
            this.isAllowToNavigateAway = true;
          } else if (event.url.indexOf('error') > 0) {
            this.isAllowToNavigateAway = true;
          } else {
            this.stickyBarCancelClick();
            this.isAllowToNavigateAway = false;
            this.destinationUrl = event.url;
          }
        }
      }
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });
  }

  private enableStickyFooterCommitButton(): void {
    this.enterBalancesValidationService.isCalendarValid = true;
    this.enterBalancesValidationService.isRecombinationTableValid = true;
  }

  private resetRCRNavigationState(): void {
    this.enterBalancesValidationService.isCalendarValid = false;
    this.enterBalancesValidationService.isRecombinationTableValid = false;
  }

  canDeactivate(): Promise<boolean> | boolean {
    return this.isAllowToNavigateAway;
  }

  private updateTitleOrTable() {
    if (this.exchangeType === StringConstants.EXCHANGE_TYPE_REMIC) {
      this.screenTitle = StringConstants.REVIEW_EXCHANGE_DETAILS;
      this.screenSubTitle = StringConstants.REVIEW_EXCHANGE_DETAILS_SUB_TEXT;
      this.displayRecomColumn = true;
      this.isAtSettlementFlow = false;
    } else if (this.exchangeType === StringConstants.EXCHANGE_TYPE_SMBS) {
      this.screenTitle = StringConstants.REVIEW_EXCHANGE_DETAILS;
      this.screenSubTitle = StringConstants.REVIEW_EXCHANGE_DETAILS_SUB_TEXT;
      this.displayRecomColumn = false;
      this.isAtSettlementFlow = false;
    } else if (this.exchangeType === StringConstants.EXCHANGE_TYPE_PREDEFINED_SMBS) {
      this.screenTitle = StringConstants.REVIEW_EXCHANGE_DETAILS;
      this.screenSubTitle = StringConstants.REVIEW_EXCHANGE_DETAILS_SUB_TEXT;
      this.displayRecomColumn = true;
      this.isAtSettlementFlow = false;
    } else if (this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_REMIC) {
      this.screenTitle = StringConstants.REVIEW_TAKEDOWN_DETAILS;
      this.screenSubTitle = StringConstants.REVIEW_TAKEDOWN_DETAILS_SUB_TEXT;
      this.displayRecomColumn = true;
      this.isAtSettlementFlow = true;
    } else if (this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_EXPD_SMBS) {
      this.screenTitle = StringConstants.REVIEW_TAKEDOWN_DETAILS;
      this.screenSubTitle = StringConstants.REVIEW_TAKEDOWN_DETAILS_SUB_TEXT;
      this.displayRecomColumn = false;
      this.isAtSettlementFlow = true;
    } else if (this.exchangeType === StringConstants.EXCHANGE_TYPE_AT_SETTLE_PREDEFINED_SMBS) {
      this.screenTitle = StringConstants.REVIEW_TAKEDOWN_DETAILS;
      this.screenSubTitle = StringConstants.REVIEW_TAKEDOWN_DETAILS_SUB_TEXT;
      this.displayRecomColumn = true;
      this.isAtSettlementFlow = true;
    }
  }

  private updateStickyBarCommitButtonText() {
    //this.stickyToolbarComponent.cancelText = StringConstants.CANCEL;
    //this.stickyToolbarComponent.showCancelButton = true;
    if (!this.isAtSettlementFlow) {
      //this.stickyToolbarComponent.buttonText = StringConstants.COMMIT_EXCHANGE;
      this.stickFooterComponents = this.commitFooter;
    } else {
      //this.stickyToolbarComponent.buttonText = StringConstants.SUBMIT_EXCHANGE;
      this.stickFooterComponents = this.submitFooter;
    }
  }

  protected hideChangeFee(arg?: string) {
    if (arg === 'save') {
      if (
        this.selectedChangeFeesItem
          ? this.selectedChangeFeesItem.id === 0
            ? this.manualFeesAmount != null
              ? true
              : false
            : true
          : true
      ) {
        if (this.selectedChangeFeesItem) {
          this.showChangeFeeModelWindow = false;
          if (this.selectedChangeFeesItem.label === 'Manual') {
            this.displayedFeeDollars = this.manualFeesAmount;
            if (this.rcrResponseData.feeCurrentFace !== 0)
              this.displayedFeeTicks =
                (this.manualFeesAmount / Number(this.rcrResponseData.feeCurrentFace)) * 3200 -
                0.005;
            else if (this.rcrResponseData.feeCurrentFace === 0)
              this.displayedFeeTicks =
                (this.manualFeesAmount / Number(this.rcrResponseData.feeCurrentFaceNotional)) *
                  3200 -
                0.005;
            this.displayedFeeTicks = Math.ceil(this.displayedFeeTicks * 100) / 100;
            this.displayedFeeTicks = Number(
              parseFloat((Math.round(this.displayedFeeTicks * 100) / 100).toString()).toFixed(2),
            );
            if (isNaN(this.displayedFeeTicks) || !isFinite(this.displayedFeeTicks)) {
              this.displayedFeeTicks = 0;
            }
          } else if (this.selectedChangeFeesItem.label === 'No Fee') {
            this.displayedFeeTicks = 0;
            this.displayedFeeDollars = 0;
          } else {
            this.displayedFeeTicks = this.selectedChangeFeesItem.label.split('/')[0];
            if (this.displayedFeeTicks === 0) this.displayedFeeDollars = 0;
            if (this.rcrResponseData.feeCurrentFace !== 0)
              this.displayedFeeDollars = Math.floor(
                (Number(this.displayedFeeTicks) / 3200) *
                  Number(this.rcrResponseData.feeCurrentFace),
              );
            else if (this.rcrResponseData.feeCurrentFace === 0)
              this.displayedFeeDollars = Math.floor(
                (Number(this.displayedFeeTicks) / 3200) *
                  Number(this.rcrResponseData.feeCurrentFaceNotional),
              );
          }
          this.displayedFeeDollars = Math.abs(this.displayedFeeDollars);
          this.overrideFeeIndicator = true;
          this.manualFeesAmount = null;
        }
        this.feeComment =
          this.tempFeeCommentStr && this.tempFeeCommentStr.replace(/^\s+|\s+$|\s+(?=\s)/g, '');
        this.selectedChangeFeesItem = null;
      }
    } else {
      this.showChangeFeeModelWindow = false;
      this.feeComment = this.feeComment && this.feeComment.replace(/^\s+|\s+$|\s+(?=\s)/g, '');
    }
  }

  showChangeFee() {
    this.showChangeFeeModelWindow = true;
  }

  toggleChangeFeeDropdown() {
    this.isChangeFeeDropdownOpen = !this.isChangeFeeDropdownOpen;
  }

  closeChangeFeeDropdown() {
    this.isChangeFeeDropdownOpen = false;
  }

  changeFeesDropDownListClick(changeFeesItem) {
    this.isChangeFeeDropdownOpen = false;
    this.selectedChangeFeesItem = changeFeesItem;
  }

  getFeeSchedule(): void {
    if (this.isFeeScheduleLinkClicked) {
      this.feeScheduleService.getExchangeFeeSchedule().subscribe({
        next: (data) => this.handleRcrFeeResponse(data as RCRFeeScheduleResponse),
        error: (error) => this.handleRcrFeeResponseError(error),
      });
    }

    this.isFeeScheduleLinkClicked = false;
  }

  private handleRcrFeeResponse(rcrFeeResponseObject: RCRFeeScheduleResponse) {
    this.isFeeScheduleLinkClicked = true;
    this.rcrService.setFeeScheduleData(rcrFeeResponseObject);
    //(Modal Pop-up for Fee Schedule) #TODO Remove Router Navigation and Show Model Popup...
    this.showFeeSheduleModelWindow = true;
    this.cdr.markForCheck();
  }

  protected closeFeeSheduleModelWindow() {
    this.showFeeSheduleModelWindow = false;
  }

  private handleRcrFeeResponseError(error: any) {
    this.isFeeScheduleLinkClicked = true;
    const errorObject = error.error;
    if (errorObject.statusCode === 400 || errorObject.statusCode === 500) {
      this.errorHandlerService.setErrorObj(errorObject);
      this.router.navigate(['error']);
    }
    this.cdr.markForCheck();
  }

  public goBack(): void {
    this.location.back();
  }

  public stickyBarBtnClick() {
    this.makeCommitServiceCall();
  }

  public stickyBarCancelClick() {
    this.showCancelExchangeConfirmation = true;
    this.isCancelConfirmation = true;
    this.isInternalUsr = this.userService.isInternalUser;
    if (!this.isAtSettlementFlow) {
      this.destinationUrl = '/rcr-exchange/lookup';
    } else {
      this.destinationUrl = '/rcr-exchange/rcr-takedown';
    }
  }

  protected cancelExchange(cancelExchange: boolean): void {
    if (cancelExchange) {
      this.isCancelConfirmation = false;
    } else {
      this.showCancelExchangeConfirmation = false;
    }
  }

  protected closeAndBackToRCRexchange(): void {
    this.clearUserService();
    this.showCancelExchangeConfirmation = false;
    this.router.navigate([this.destinationUrl]);
    this.isAllowToNavigateAway = true;
  }

  private clearUserService(): void {
    this.userService.setTrader(null, null);
    this.userService.setDealerName(null);
  }

  private makeCommitServiceCall() {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    const commitRequest: any = new Object();
    commitRequest.dealNumber =
      this.passDataService.getRecombinationDataCacheWithoutNullGroup().dealNumber;
    commitRequest.dealSettlementDate =
      this.passDataService.getRecombinationDataCacheWithoutNullGroup().dealSettlementDate;
    commitRequest.dealSubType =
      this.passDataService.getRecombinationDataCacheWithoutNullGroup().dealSubType;
    commitRequest.exchangeType =
      this.passDataService.getRecombinationDataCacheWithoutNullGroup().exchangeType;
    commitRequest.exchangeSettlementDate =
      this.passDataService.getRecombinationDataCacheWithoutNullGroup().exchangeSettlementDate;
    commitRequest.exchangeConfirmationNumber = this.passDataService.getExchangeConfirmationNumber();
    commitRequest.feeTicks = this.rcrResponseData.feeTicks;
    commitRequest.feeDollars = this.rcrResponseData.feeDollars;
    commitRequest.overrideFeeIndicator = this.overrideFeeIndicator ? 'Y' : 'N';
    commitRequest.overrideFeeTicks = this.overrideFeeIndicator ? this.displayedFeeTicks : null;
    commitRequest.overrideFeeDollars = this.overrideFeeIndicator ? this.displayedFeeDollars : null;
    commitRequest.feeComment = this.feeComment;
    commitRequest.recombinations =
      this.passDataService.getRecombinationDataCacheWithoutNullGroup().recombinations;

    commitRequest.sttsTraderId = this.userService.getTraderId();
    /*
    if (this.userService.isRCRInternal()) {
      commitRequest['sttsTraderId'] = this.userService.getTraderId();
    } else {
      commitRequest['sttsTraderId'] = null;
    }
    */
    commitRequest.isMinOverride = this.passDataService.getMinOverride();
    commitRequest.isPndIerrOverride = this.passDataService.getPndIerrOverride();
    commitRequest.overrideErrorMsgs = this.passDataService.getEmailErrors();

    this.rcrService.commitExchangeService(commitRequest).subscribe({
      next: (commitExchResponse) => {
        if (commitExchResponse.hasOwnProperty('errorCode')) {
          //alert(commitExchResponse['displayMessage'] + commitExchResponse['resolution']);
          this.onErrorCommitExchangeResponse(commitExchResponse);
        } else {
          this.onNextCommitExchangeResponse(commitExchResponse as CommitExchangeResponse);
        }
      },
      error: (error) => {
        this.onErrorCommitExchangeResponse(error);
      },
    });
  }

  private onErrorCommitExchangeResponse(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.passDataService.setEmailErrors([]);
    this.cdr.markForCheck();
    // let errorObject = null;
    // if (error.hasOwnProperty('json')) {
    //   errorObject = error.json();
    // } else if (error.hasOwnProperty('_body')) {
    //   errorObject = JSON.parse(error._body);
    // } else {
    //   errorObject = error;
    // }
  }

  private onNextCommitExchangeResponse(commitExchangeResponseObject: CommitExchangeResponse): void {
    this.rcrNavigationService.canNavigate = true;
    this.passDataService.setEmailErrors([]);
    this.passDataService.setCommitExchangeResponse(commitExchangeResponseObject);
    this.fmLoadingPageService.stopLoading();
    this.router.navigate([this.router.url, 'confirmation']);
  }

  private displayBanner(): void {
    if (
      this.rcrResponseData &&
      this.rcrResponseData.infoMessages &&
      this.rcrResponseData.infoMessages !== null
    ) {
      this.showBanner = true;
      this.warningMessages = [];
      this.rcrResponseData.infoMessages.map((messageInfo) => {
        const title = 'Status';
        /*messageInfo.split('.')[0] + '.';*/
        const message = messageInfo;
        /*messageInfo.split('.')[1] + '.';*/
        const type = StringConstants.MESSAGE_NOTICE;
        this.warningMessages.push({ title, message, type });
      });
    }
  }

  private displayPricingModel(): void {
    const date =
      this.passDataService.getRecombinationDataCacheWithoutNullGroup().dealSettlementDate;
    const dealSubType =
      this.passDataService.getRecombinationDataCacheWithoutNullGroup().dealSubType;
    const dealNumber = this.passDataService.getRecombinationDataCacheWithoutNullGroup().dealNumber;
    if (
      new Date('01/01/2007') > new Date(date) &&
      dealSubType === 'SMBS' &&
      (this.exchangeType === StringConstants.EXCHANGE_TYPE_REMIC ||
        this.exchangeType === StringConstants.EXCHANGE_TYPE_SMBS ||
        this.exchangeType === StringConstants.EXCHANGE_TYPE_PREDEFINED_SMBS)
    ) {
      this.displayPricingRule = true;
      this.prospectSupplURL =
        'https://fanniemae.mbs-securities.com/fannie/deal/' + 'TR000' + dealNumber;
    }
  }

  private listenToBannerExitReviewAndCommitClick(): void {
    this.dateSelectedService.bannerExitClickInReviewAndCommit$.subscribe((_exitClicked) => {
      this.showBannerExitIconNavigationPopup = true;
    });
  }

  protected goToRCRRequestExchangePage(): void {
    this.router.navigate(['rcr-exchange/lookup']);
    this.isAllowToNavigateAway = true;
  }

  public allowedText(): void {
    this.tempFeeCommentStr = this.tempFeeCommentStr.replace(/[^a-zA-Z0-9$., ]/g, '');
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
